import {convertLexicalToHTML, defaultHTMLConverters} from '@payloadcms/richtext-lexical/html'
import {convertLexicalToPlaintext} from '@payloadcms/richtext-lexical/plaintext'
import type {SerializedEditorState} from 'lexical'

/** The Lexical rich‑text JSON shape used across Payload types. */
export type LexicalData = SerializedEditorState

/**
 * A rich text field value — either Lexical JSON (from a `richText` field)
 * or a plain HTML string (legacy / already serialised).
 */
export type RichTextValue = LexicalData | string | null | undefined

/** Type guard: does `value` look like a serialised Lexical editor state? */
export function isLexicalData(value: unknown): value is LexicalData {
  return (
    typeof value === 'object' &&
    value !== null &&
    'root' in value &&
    typeof (value as Record<string, unknown>).root === 'object'
  )
}

/**
 * Convert a rich‑text field value to an HTML string.
 *
 * - If `value` is already a string it is returned as-is (legacy / pre-serialised HTML).
 * - If `value` is a Lexical `SerializedEditorState` it is converted on the fly.
 * - `null` / `undefined` → `''`.
 */
export function lexicalToHtml(value: RichTextValue): string {
  if (value == null) return ''
  if (typeof value === 'string') return value

  if (!isLexicalData(value)) return ''

  return convertLexicalToHTML({
    converters: defaultHTMLConverters,
    data: value,
    disableContainer: true,
  })
}

/**
 * Convert a rich‑text field value to plain text (useful for schema.org, meta descriptions, etc.).
 *
 * Falls back to stripping HTML tags when the value is already a string.
 */
export function lexicalToPlaintext(value: RichTextValue): string {
  if (value == null) return ''

  if (typeof value === 'string') {
    // Strip HTML tags from legacy string values
    return value.replace(/<[^>]*>/g, '').trim()
  }

  if (!isLexicalData(value)) return ''

  return convertLexicalToPlaintext({data: value})
}
